import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Button,
  Input,
  Label,
  FormGroup,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import axios from "axios";
import BASE_URL from "../../config";
import Select from "react-select";
import { 
  hasEditOrHigherPermission, 
  hasDeletePermissionWithAdmin,
  hasPagePermissionWithAdmin
} from "../../components/Permission/PermissionService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

// API Helpers
import { getRooms } from "../../components/Api/GmsRoom";
import { getEquipments } from "../../components/Api/GmsEquipment";
import { getEmployees, searchEmployees } from "../../components/Api/GmsEmployee";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getTicketNotifications, markTicketNotificationAsRead } from "../../components/Api/GmsTicket";

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || authUser.user_Id || 1,
    userName: authUser.userName || "System",
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
    employeeId: authUser.employeeId || user.user_EmployeeId || null,
    isAdmin: authUser.isAdmin || false
  };
};

const GmsMaintenanceTicketsPage = (props) => {
  const session = useMemo(() => getUserSession(), []);
  const [tickets, setTickets] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // Permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isEditUser = session.isAdmin || hasEditOrHigherPermission(permissions, '/GmsMaintenanceTickets');
  
  // Form State
  const [formData, setFormData] = useState({
    ticketType: 'M',
    businessId: null,
    branchId: null,
    roomId: null,
    equipmentId: null,
    priority: 2,
    description: '',
    comments: '',
    assignedTo: null,
    documentFile: null
  });

  // Update Modal State
  const [updateData, setUpdateData] = useState({
    statusId: 1,
    comment: '',
    assignedTo: null
  });

  // View Modal State
  const [viewModal, setViewModal] = useState(false);
  const [ticketLogs, setTicketLogs] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  // Technician Search State
  const [technicianSearchOptions, setTechnicianSearchOptions] = useState([]);
  const [technicianSearchLoading, setTechnicianSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const priorityOptions = [
    { value: 0, label: 'Critical', color: 'danger' },
    { value: 1, label: 'High', color: 'warning' },
    { value: 2, label: 'Medium', color: 'info' },
    { value: 3, label: 'Low', color: 'secondary' }
  ];

  const statusList = [
    { value: 1, label: 'Open', color: 'danger' },
    { value: 2, label: 'Assigned', color: 'info' },
    { value: 3, label: 'OnHold', color: 'warning' },
    { value: 4, label: 'Cancelled', color: 'secondary' },
    { value: 5, label: 'Reopened', color: 'primary' },
    { value: 6, label: 'Closed', color: 'success' },
    { value: 7, label: 'Done', color: 'success' }
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  // When business/branch changes, load data
  useEffect(() => {
    if (formData.businessId && formData.branchId) {
      loadTickets();
    } else {
      setTickets([]);
    }
  }, [formData.businessId, formData.branchId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [busRes, roomsRes, equipRes, empRes] = await Promise.all([
        getBusinesses({ business_Active: true }),
        getRooms({ Status: true }),
        getEquipments({ status: true }),
        getEmployees({ Status: 1 })
      ]);

      const busList = busRes.data || busRes || [];
      setBusinesses(busList);
      setRooms((roomsRes.data || roomsRes || []).map(r => ({ value: r.room_Id, label: r.room_Name_English })));
      setEquipments((equipRes.data || equipRes || []).map(e => ({ value: e.eq_Id || e.equipment_Id, label: e.eq_Name_English || e.equipment_Name_English })));
      setEmployees((empRes.data || empRes || []).map(e => ({ value: e.employee_Id, label: e.employee_NameEn || e.employee_Name_En })));

      // Session logic for businesses/branches
      let initialBusId = null;
      let initialBrId = null;

      if (session.companyId) {
        initialBusId = typeof session.companyId === 'string' ? parseInt(session.companyId) : session.companyId;
        await loadBranchesForBusiness(initialBusId);
      }
      
      if (session.branchId) {
        initialBrId = typeof session.branchId === 'string' ? parseInt(session.branchId) : session.branchId;
      }

      setFormData(prev => ({ 
        ...prev, 
        businessId: initialBusId, 
        branchId: initialBrId 
      }));

    } catch (error) {
      console.error("Error loading initial data", error);
      toast.error("Failed to load setup data");
    } finally {
      setLoading(false);
    }
  };

  const loadBranchesForBusiness = async (businessId) => {
    if (!businessId) {
       setBranches([]);
       return;
    }
    try {
      const branchesRes = await getBranches({ Business_Id: businessId, branch_Active: true });
      setBranches(branchesRes.data || branchesRes || []);
    } catch (error) {
      console.error("Error loading branches", error);
      setBranches([]);
    }
  };

  const searchTechniciansManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setTechnicianSearchOptions([]);
      return;
    }

    try {
      setTechnicianSearchLoading(true);
      // Pass the selected company and branch from current formData
      const response = await searchEmployees(inputValue, 1, 30, formData.businessId, formData.branchId);

      const formattedOptions = response.items.map(employee => ({
        value: employee.employee_Id,
        label: `${employee.employee_NameEn || 'N/A'} — ${employee.emp_No || employee.Emp_No || 'N/A'}`,
      }));

      setTechnicianSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching technicians:', error);
      setTechnicianSearchOptions([]);
    } finally {
      setTechnicianSearchLoading(false);
    }
  };

  const loadTickets = async () => {
    if (!formData.businessId || !formData.branchId) return;
    try {
      const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      
      const payload = { 
        BusinessID: formData.businessId,
        BranchID: formData.branchId
      };

      // If user is not Admin/Edit user, only show tickets assigned to them
      if (!isEditUser && session.employeeId) {
        payload.AssignedTo = session.employeeId;
      }

      const response = await axios.post(`${BASE_URL}/api/Ticket/get`, payload, authHeader);
      setTickets(response.data || []);
    } catch (error) {
      console.error("Error loading tickets", error);
    }
  };

  const handleCreateTicket = async () => {
    if (!formData.description) return toast.error("Description is required");
    if (!formData.businessId) return toast.error("Company is required");
    if (!formData.branchId) return toast.error("Branch is required");

    try {
      setLoading(true);
      const authHeader = { headers: { 
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        'Content-Type': 'multipart/form-data'
      } };

      const ticketFormData = new FormData();
      ticketFormData.append('ticketType', formData.ticketType);
      ticketFormData.append('businessId', formData.businessId);
      ticketFormData.append('branchId', formData.branchId || '');
      ticketFormData.append('roomId', formData.roomId?.value || '');
      ticketFormData.append('equipmentId', formData.equipmentId?.value || '');
      ticketFormData.append('priority', formData.priority);
      ticketFormData.append('description', formData.description);
      ticketFormData.append('comment', formData.comments);
      ticketFormData.append('assignedTo', formData.assignedTo?.value || '');
      ticketFormData.append('createdBy', session.userId);

      if (formData.documentFile) {
        ticketFormData.append('documentFile', formData.documentFile);
      }

      await axios.post(`${BASE_URL}/api/Ticket/save`, ticketFormData, authHeader);
      toast.success("Ticket logged successfully");
      
      // Reset Form (keeping business/branch)
      setFormData(prev => ({
        ...prev,
        roomId: null,
        equipmentId: null,
        priority: 2,
        description: '',
        comments: '',
        assignedTo: null,
        documentFile: null
      }));
      // Clear file input
      const fileInput = document.getElementById('ticketDocument');
      if (fileInput) fileInput.value = '';
      
      loadTickets();
    } catch (error) {
      console.error("Error saving ticket", error);
      toast.error("Failed to log ticket");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTicket = async () => {
    try {
      setLoading(true);
      const authHeader = { headers: { 
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        'Content-Type': 'multipart/form-data'
      } };

      const updateFormData = new FormData();
      updateFormData.append('ticketID', selectedTicket.ticketID);
      updateFormData.append('statusID', updateData.statusId);
      updateFormData.append('comment', updateData.comment);
      // Only Admin/Edit user can change technician
      const finalAssignedTo = isEditUser ? (updateData.assignedTo?.value || selectedTicket.assignedTo || '') : (selectedTicket.assignedTo || '');

      if (!finalAssignedTo && updateData.statusId !== 1 && updateData.statusId !== 4) {
         toast.warning("A ticket must have an assigned technician for statuses other than Open or Cancelled.");
         setLoading(false);
         return;
      }
      
      updateFormData.append('assignedTo', finalAssignedTo);
      updateFormData.append('businessId', formData.businessId);
      updateFormData.append('branchId', formData.branchId || '');
      updateFormData.append('createdBy', session.userId);
      updateFormData.append('priority', selectedTicket.priority);
      updateFormData.append('description', selectedTicket.description);
      updateFormData.append('ticketType', selectedTicket.ticketType);

      await axios.post(`${BASE_URL}/api/Ticket/save`, updateFormData, authHeader);
      toast.success("Ticket updated successfully");
      setModal(false);
      loadTickets();
    } catch (error) {
      console.error("Error updating ticket", error);
      toast.error("Failed to update ticket");
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (p) => {
    const opt = priorityOptions.find(o => o.value === p);
    return <Badge color={`soft-${opt?.color || "secondary"}`} className={`text-${opt?.color || "secondary"} px-2 py-1`}>{opt?.label || "Unknown"}</Badge>;
  };

  const getStatusBadge = (s) => {
    const opt = statusList.find(o => o.value === s);
    return <Badge color={`soft-${opt?.color || "secondary"}`} pill className={`text-${opt?.color || "secondary"} px-2 py-1`}>{opt?.label || "Unknown"}</Badge>;
  };

  const toggleModal = (ticket) => {
    if (ticket) {
      setSelectedTicket(ticket);
      setUpdateData({
        statusId: ticket.statusId,
        comment: '',
        assignedTo: ticket.assignedTo ? { value: ticket.assignedTo, label: ticket.assignedToName } : null
      });
    }
    setModal(!modal);
  };

  const toggleViewModal = async (ticket) => {
    if (ticket) {
      setSelectedTicket(ticket);
      setViewLoading(true);
      setViewModal(true);
      setTicketLogs([]); // Reset logs
      try {
        const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
        const logsRes = await axios.get(`${BASE_URL}/api/Ticket/logs/${ticket.ticketID}`, authHeader);
        const logs = logsRes.data || [];
        setTicketLogs(logs);

        // Mark unread logs as read for the current user using the stable session
        const userId = session.userId;
        const employeeId = session.employeeId;
        
        const unreadLogs = logs.filter(l => 
          l.isRead === false && 
          (l.notificationRecipientID === userId || l.notificationRecipientID === employeeId)
        );

        for (const log of unreadLogs) {
          try {
            await markTicketNotificationAsRead(log.logID, log.notificationRecipientID);
          } catch (e) {
             console.error("Error marking as read", e);
          }
        }
      } catch (error) {
        console.error("Error loading logs", error);
        toast.error("Failed to load ticket history");
      } finally {
        setViewLoading(false);
      }
    } else {
      setViewModal(false);
    }
  };

  const handleViewDocument = async (ticket) => {
    try {
      const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      const docRes = await axios.get(`${BASE_URL}/api/Ticket/documents/${ticket.ticketID}`, authHeader);
      const docs = docRes.data || [];
      if (docs.length > 0) {
        window.open(`${BASE_URL}${docs[0].documentPath}`, '_blank');
      } else {
        toast.info("No document found for this ticket.");
      }
    } catch (error) {
      console.error("Error loading documents", error);
      toast.error("Failed to load document path");
    }
  };

  const businessOptions = businesses.map(b => ({ value: b.business_Id, label: b.business_NameEn || b.business_Name }));
  const branchOptions = branches.map(b => ({ value: b.branch_Id, label: b.branch_NameEn || b.branch_Name }));
  const isBusinessDisabled = !!session.companyId;
  const isBranchDisabled = !!session.branchId;

  // Metrics
  const metrics = {
    open: tickets.filter(t => t.statusId === 1).length,
    assigned: tickets.filter(t => t.statusId === 2).length,
    inProgress: tickets.filter(t => t.statusId === 3).length,
    closedTotal: tickets.filter(t => t.statusId === 6).length
  };

  const isSelected = formData.businessId && formData.branchId;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="GMS" breadcrumbItem="Maintenance" />
          
          {/* Top Filter Selection (Selection First Policy) */}
          <Row>
            <Col lg={12}>
              <Card className="shadow-sm border-0 mb-4">
                <CardBody className="py-3">
                  <Row className="align-items-center">
                    <Col md={6}>
                      <FormGroup className="mb-0 d-flex align-items-center">
                        <Label className="me-3 mb-0 flex-shrink-0">Company <span className="text-danger">*</span></Label>
                        <Select
                          value={businessOptions.find(o => o.value === formData.businessId)}
                          onChange={(opt) => {
                            setFormData({ ...formData, businessId: opt?.value, branchId: null });
                            loadBranchesForBusiness(opt?.value);
                          }}
                          options={businessOptions}
                          isDisabled={isBusinessDisabled}
                          styles={{...selectStyles, container: base => ({...base, flexGrow: 1})}}
                          placeholder="Select Company..."
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-0 d-flex align-items-center">
                        <Label className="me-3 mb-0 flex-shrink-0">Branch <span className="text-danger">*</span></Label>
                        <Select
                          value={branchOptions.find(o => o.value === formData.branchId)}
                          onChange={(opt) => setFormData({ ...formData, branchId: opt?.value })}
                          options={branchOptions}
                          isDisabled={isBranchDisabled || !formData.businessId}
                          styles={{...selectStyles, container: base => ({...base, flexGrow: 1})}}
                          placeholder="Select Branch..."
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {isSelected ? (
            <>
              {/* Dashboard Metrics - Optimized Tile Pattern */}
              <Row className="mb-4">
                <Col lg={12}>
                  <Card className="shadow-sm border border-primary border-2">
                    <CardBody className="p-2">
                      <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '0.75rem' }}>
                        {[
                          { label: 'Open Issues', value: metrics.open, color: 'danger', icon: 'bx-error' },
                          { label: 'Assigned', value: metrics.assigned, color: 'info', icon: 'bx-user-plus' },
                          { label: 'In Progress', value: metrics.inProgress, color: 'warning', icon: 'bx-loader' },
                          { label: 'Closed', value: metrics.closedTotal, color: 'success', icon: 'bx-check-circle' }
                        ].map((m, idx) => (
                          <div key={idx} style={{ flex: '1', minWidth: '180px' }}>
                             <div className="text-center p-2 h-100" style={{ backgroundColor: '#f8f9fa', borderRadius: '0.5rem', border: '1px solid #f1f3f5' }}>
                                <div className="d-flex align-items-center justify-content-center py-1">
                                   <div className={`avatar-sm bg-soft-${m.color} text-${m.color} rounded-circle d-flex align-items-center justify-content-center me-3`} style={{ width: '40px', height: '40px' }}>
                                      <i className={`bx ${m.icon} fs-4`}></i>
                                   </div>
                                   <div className="text-start">
                                      <p className="text-muted fw-bold text-uppercase mb-0" style={{ fontSize: '11px', letterSpacing: '0.8px' }}>{m.label}</p>
                                      <h5 className={`mb-0 text-${m.color} fw-bold`} style={{ fontSize: '20px' }}>{m.value}</h5>
                                   </div>
                                </div>
                             </div>
                          </div>
                        ))}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                {/* Left Column: Log Issue Form - Visible only to Edit users */}
                {isEditUser && (
                  <Col lg={4}>
                    <Card className="shadow-sm border border-info border-2 h-100">
                      <CardBody>
                        <CardTitle className="mb-4 text-info fw-bold d-flex align-items-center">
                          <i className="bx bx-plus-circle me-2 fs-3"></i> Log New Ticket
                        </CardTitle>
                        <Form>
                          <Row>
                            <Col md={12}>
                              <FormGroup>
                                <Label className="fw-bold">Ticket Type</Label>
                                <div className="d-flex gap-4 mb-2">
                                   <div className="form-check">
                                      <Input 
                                        type="radio" 
                                        id="typeM" 
                                        className="form-check-input" 
                                        checked={formData.ticketType === 'M'} 
                                        onChange={() => setFormData({ ...formData, ticketType: 'M' })} 
                                      />
                                      <Label className="form-check-label" htmlFor="typeM">Maintenance</Label>
                                   </div>
                                   <div className="form-check">
                                      <Input 
                                        type="radio" 
                                        id="typeT" 
                                        className="form-check-input" 
                                        checked={formData.ticketType === 'T'} 
                                        onChange={() => setFormData({ ...formData, ticketType: 'T' })} 
                                      />
                                      <Label className="form-check-label" htmlFor="typeT">Normal Ticket</Label>
                                   </div>
                                </div>
                              </FormGroup>
                            </Col>

                            <Col md={6}>
                              <FormGroup className="mb-4">
                                <Label className="fw-bold">Room</Label>
                                <Select
                                  value={formData.roomId}
                                  onChange={(opt) => setFormData({ ...formData, roomId: opt })}
                                  options={rooms}
                                  isClearable
                                  isDisabled={formData.ticketType === 'T'}
                                  styles={selectStyles}
                                  placeholder="Select"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup className="mb-4">
                                <Label className="fw-bold">Equipment</Label>
                                <Select
                                  value={formData.equipmentId}
                                  onChange={(opt) => setFormData({ ...formData, equipmentId: opt })}
                                  options={equipments}
                                  isClearable
                                  isDisabled={formData.ticketType === 'T'}
                                  styles={selectStyles}
                                  placeholder="Select"
                                />
                              </FormGroup>
                            </Col>

                            <Col md={12}>
                              <FormGroup className="mb-4">
                                <Label className="fw-bold">Assignee</Label>
                                <Select
                                   value={formData.assignedTo}
                                   onChange={(opt) => setFormData({ ...formData, assignedTo: opt })}
                                   options={technicianSearchOptions}
                                   onInputChange={(inputValue, { action }) => {
                                      if (action === 'input-change') {
                                         if (searchTimeout) clearTimeout(searchTimeout);
                                         const timeout = setTimeout(() => {
                                            searchTechniciansManually(inputValue);
                                         }, 400);
                                         setSearchTimeout(timeout);
                                      }
                                   }}
                                   isLoading={technicianSearchLoading}
                                   noOptionsMessage={({ inputValue }) => {
                                      if (technicianSearchLoading) return "Searching...";
                                      if (!inputValue || inputValue.length < 3) return "Type at least 3 characters to search";
                                      return "No technicians found";
                                   }}
                                   isClearable
                                   styles={selectStyles}
                                   placeholder="Search Tech..."
                                />
                              </FormGroup>
                            </Col>

                            <Col md={12}>
                              <FormGroup className="mb-4">
                                <Label className="fw-bold">Issue Description <span className="text-danger">*</span></Label>
                                <Input
                                  type="textarea"
                                  rows="2"
                                  placeholder="Describe the problem..."
                                  value={formData.description}
                                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                />
                              </FormGroup>
                            </Col>

                            <Col md={12}>
                              <FormGroup className="mb-4">
                                <Label className="fw-bold">Comments / Remarks</Label>
                                <Input
                                  type="textarea"
                                  rows="2"
                                  placeholder="Notes..."
                                  value={formData.comments}
                                  onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                                />
                              </FormGroup>
                            </Col>

                            <Col md={12}>
                              <FormGroup className="mb-4">
                                <Label className="fw-bold">Priority</Label>
                                <div className="d-flex gap-3 mt-1">
                                  {priorityOptions.map((opt) => (
                                    <div key={opt.value} className="form-check form-check-inline">
                                      <Input
                                        type="radio"
                                        name="priority_radio"
                                        className="form-check-input"
                                        checked={formData.priority === opt.value}
                                        onChange={() => setFormData({ ...formData, priority: opt.value })}
                                      />
                                      <Label className="form-check-label small">{opt.label}</Label>
                                    </div>
                                  ))}
                                </div>
                              </FormGroup>
                            </Col>

                            <Col md={12}>
                              <FormGroup className="mb-4">
                                <Label className="fw-bold">Upload Attachment</Label>
                                <Input
                                  type="file"
                                  id="ticketDocument"
                                  className="form-control"
                                  onChange={(e) => setFormData({ ...formData, documentFile: e.target.files[0] })}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <Button 
                            color="info" 
                            block 
                            className="mt-3 py-2 fw-bold" 
                            onClick={handleCreateTicket} 
                            disabled={loading}
                          >
                            {loading ? <i className="bx bx-loader bx-spin me-2"></i> : <i className="bx bx-save me-2"></i>}
                            Submit Ticket
                          </Button>
                        </Form>
                      </CardBody>
                    </Card>
                  </Col>
                )}

                {/* Right Column: Tickets Table */}
                <Col lg={isEditUser ? 8 : 12}>
                  <Card className="shadow-sm border border-warning border-2 mb-4">
                    <CardBody className="pb-3">
                      <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                         <span className="text-warning fw-bold d-flex align-items-center">
                            <i className="bx bx-list-ul me-2 fs-3"></i> {isEditUser ? 'Active Tickets' : 'My Assigned Tickets'}
                         </span>
                        <Button size="sm" color="light" onClick={loadTickets} disabled={loading}>
                          <i className={`bx bx-refresh ${loading ? 'bx-spin' : ''}`}></i>
                        </Button>
                      </CardTitle>
                      <div style={{ maxHeight: '350px', overflowY: 'auto', borderBottom: '1px solid #f1f3f5' }}>
                        <Table className="table-nowrap table-hover mb-0 align-middle w-100">
                          <thead className="table-light text-warning sticky-top" style={{ zIndex: 5, top: 0 }}>
                            <tr style={{ position: 'sticky', top: 0, zIndex: 6 }}>
                              <th>Code</th>
                              <th>Room/Equip</th>
                              <th>Priority</th>
                              <th>Status</th>
                              {isEditUser && <th>Assignee</th>}
                              <th className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tickets.filter(t => t.statusId !== 6).map((ticket) => (
                              <tr key={ticket.ticketID}>
                                <td className="fw-bold text-primary">#{ticket.ticketCode}</td>
                                <td>
                                   <div className="fw-medium">
                                      {ticket.roomName && ticket.equipmentName ? `${ticket.roomName} - ${ticket.equipmentName}` : (ticket.roomName || ticket.equipmentName || "-")}
                                   </div>
                                </td>
                                <td>{getPriorityBadge(ticket.priority)}</td>
                                <td>{getStatusBadge(ticket.statusId)}</td>
                                {isEditUser && <td>{ticket.assignedToName || <span className="text-danger small italic text-uppercase" style={{fontSize: '0.7rem'}}>Unassigned</span>}</td>}
                                <td className="text-center">
                                   <div className="d-flex gap-2 justify-content-center">
                                      <Button
                                        size="sm"
                                        color="soft-info"
                                        id={`view-${ticket.ticketID}`}
                                        className="btn-rounded"
                                        onClick={() => toggleViewModal(ticket)}
                                      >
                                        <i className="bx bx-show-alt"></i>
                                      </Button>
                                      {ticket.documentCount > 0 && (
                                        <Button
                                          size="sm"
                                          color="soft-dark"
                                          className="btn-rounded"
                                          onClick={() => handleViewDocument(ticket)}
                                        >
                                          <i className="bx bx-file"></i>
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        color="soft-primary"
                                        className="btn-rounded"
                                        onClick={() => toggleModal(ticket)}
                                      >
                                        <i className="bx bx-edit"></i>
                                      </Button>
                                   </div>
                                </td>
                              </tr>
                            ))}
                            {tickets.filter(t => t.statusId !== 6).length === 0 && (
                              <tr>
                                <td colSpan={isEditUser ? "6" : "5"} className="text-center py-5 text-muted italic small">No active tickets for the selected branch.</td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>

                  {/* Completed History */}
                  <Card className="shadow-sm border border-success border-2">
                    <CardBody className="pb-3">
                      <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                         <span className="text-success fw-bold d-flex align-items-center">
                            <i className="bx bx-history me-2 fs-3"></i> Resolved Issues History
                         </span>
                      </CardTitle>
                      <div style={{ maxHeight: '350px', overflowY: 'auto', borderBottom: '1px solid #f1f3f5' }}>
                        <Table className="table-nowrap table-hover mb-0 align-middle w-100">
                          <thead className="table-light text-success sticky-top" style={{ zIndex: 5, top: 0 }}>
                            <tr style={{ position: 'sticky', top: 0, zIndex: 6 }}>
                              <th>Code</th>
                              <th>Room/Equip</th>
                              {isEditUser && <th>Assignee</th>}
                              <th>Priority</th>
                              <th>Resolved Date</th>
                              <th className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tickets.filter(t => t.statusId === 6).map((ticket) => (
                              <tr key={ticket.ticketID}>
                                <td className="fw-bold text-primary">#{ticket.ticketCode}</td>
                                <td>
                                   <div className="fw-medium">{ticket.roomName || ticket.equipmentName || "-"}</div>
                                </td>
                                {isEditUser && <td className="fw-medium">{ticket.assignedToName || "N/A"}</td>}
                                <td>{getPriorityBadge(ticket.priority)}</td>
                                <td className="fw-medium text-muted">
                                   {new Date(ticket.resolvedDate).toLocaleDateString()}
                                </td>
                                <td className="text-center">
                                   <div className="d-flex gap-2 justify-content-center">
                                      <Button
                                        size="sm"
                                        color="soft-info"
                                        className="btn-rounded"
                                        onClick={() => toggleViewModal(ticket)}
                                      >
                                        <i className="bx bx-show-alt"></i>
                                      </Button>
                                      {ticket.documentCount > 0 && (
                                        <Button
                                          size="sm"
                                          color="soft-dark"
                                          className="btn-rounded"
                                          onClick={() => handleViewDocument(ticket)}
                                        >
                                          <i className="bx bx-file"></i>
                                        </Button>
                                      )}
                                   </div>
                                </td>
                              </tr>
                            ))}
                            {tickets.filter(t => t.statusId === 6).length === 0 && (
                              <tr>
                                <td colSpan={isEditUser ? "5" : "4"} className="text-center py-5 text-muted italic small">No history records found.</td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <Row>
               <Col lg={12} className="text-center mt-5">
                  <div className="p-5">
                     <i className="bx bx-search-alt text-muted display-1 mb-4"></i>
                     <h3 className="text-muted">Select Company and Branch</h3>
                  </div>
               </Col>
            </Row>
          )}

          {/* Update Modal */}
          <Modal isOpen={modal} toggle={() => setModal(!modal)} centered>
            <ModalHeader toggle={() => setModal(!modal)} className="bg-primary text-white" style={{ color: '#fff' }}>
              <span className="text-white">Update Ticket #{selectedTicket?.ticketCode}</span>
            </ModalHeader>
            <ModalBody className="p-4">
              <div className="mb-3">
                <Label className="fw-bold text-primary">Update Status</Label>
                <Input
                  type="select"
                  value={updateData.statusId}
                  onChange={(e) => setUpdateData({ ...updateData, statusId: parseInt(e.target.value) })}
                  className="form-select border-primary"
                >
                  {statusList.map((s) => {
                    const isAdminOrCreator = isEditUser || session.userId === selectedTicket?.createdBy;
                    const isAssignee = session.employeeId === selectedTicket?.assignedTo;
                    const curr = selectedTicket?.statusId;
                    
                    let allowed = false;
                    
                    if (s.value === curr) {
                        allowed = true; // Always allow current state
                    } else if (isAdminOrCreator) {
                        // Admin / Creator Workflow
                        if (curr === 1) allowed = [2, 3, 4].includes(s.value);
                        else if (curr === 2 || curr === 5 || curr === 3) allowed = [1, 2, 3, 4, 7].includes(s.value);
                        else if (curr === 7) allowed = [6, 5, 2, 3, 4].includes(s.value);
                        else if (curr === 6) allowed = [5].includes(s.value);
                    } else if (isAssignee && !isAdminOrCreator) {
                        // Technician Workflow
                        if (curr === 2 || curr === 5) allowed = [3, 7].includes(s.value);
                        else if (curr === 3) allowed = [2, 7].includes(s.value);
                    }
                    
                    if (!allowed) return null;
                    return <option key={s.value} value={s.value}>{s.label}</option>;
                  })}
                </Input>
              </div>
              
              {/* Only Admin/Edit user can change technician */}
              {isEditUser && (
                <div className="mb-3">
                  <Label className="fw-bold text-primary">Change Assignee (Optional)</Label>
                  <Select
                    value={updateData.assignedTo}
                    onChange={(opt) => setUpdateData({ ...updateData, assignedTo: opt })}
                    options={technicianSearchOptions}
                    onInputChange={(inputValue, { action }) => {
                      if (action === 'input-change') {
                        if (searchTimeout) clearTimeout(searchTimeout);
                        const timeout = setTimeout(() => {
                          searchTechniciansManually(inputValue);
                        }, 400);
                        setSearchTimeout(timeout);
                      }
                    }}
                    isLoading={technicianSearchLoading}
                    noOptionsMessage={({ inputValue }) => {
                      if (technicianSearchLoading) return "Searching...";
                      if (!inputValue || inputValue.length < 3) return "Type at least 3 characters to search";
                      return "No technicians found";
                    }}
                    placeholder="Search & Select..."
                    styles={selectStyles}
                    isClearable
                  />
                </div>
              )}

              <div className="mb-3">
                <Label className="fw-bold text-primary">Update Remarks</Label>
                <Input
                  type="textarea"
                  rows="3"
                  value={updateData.comment}
                  onChange={(e) => setUpdateData({ ...updateData, comment: e.target.value })}
                  placeholder="Detail the work done or reason for hold..."
                  className="border-primary"
                />
              </div>
            </ModalBody>
            <ModalFooter className="bg-light">
              <Button color="secondary" outline onClick={() => setModal(false)}>Cancel</Button>
              <Button color="primary" onClick={handleUpdateTicket}>Save Changes</Button>
            </ModalFooter>
          </Modal>

          {/* View Ticket Details Modal */}
          <Modal isOpen={viewModal} toggle={() => setViewModal(!viewModal)} size="lg" centered>
            <ModalHeader toggle={() => setViewModal(!viewModal)} className="bg-info text-white" style={{ color: '#fff' }}>
              <span className="text-white">Ticket Detail - #{selectedTicket?.ticketCode}</span>
            </ModalHeader>
            <ModalBody className="bg-light p-0">
               <div className="p-4 bg-white border-bottom shadow-sm">
                  <Row>
                     <Col md={8}>
                        <h6 className="text-muted text-uppercase fw-bold small mb-2">Issue Description</h6>
                        <p className="fs-5 mb-0 fw-medium text-dark">{selectedTicket?.description}</p>
                     </Col>
                     <Col md={4} className="text-md-end">
                        <div className="mb-2">{selectedTicket && getStatusBadge(selectedTicket.statusId)}</div>
                        <div className="small text-muted">Priority: {selectedTicket && getPriorityBadge(selectedTicket.priority)}</div>
                     </Col>
                  </Row>
                  <hr className="my-3"/>
                  <Row className="small">
                     <Col md={4}>
                        <i className="bx bx-map-pin me-1 text-info"></i>
                        <span className="text-muted">Room/Eq:</span> <span className="fw-bold text-dark ms-1">{selectedTicket?.roomName ? selectedTicket.roomName : (selectedTicket?.equipmentName ? "" : "-")} {selectedTicket?.equipmentName ? ` ${selectedTicket.equipmentName}` : ''}</span>
                     </Col>
                     <Col md={4}>
                        <i className="bx bx-user me-1 text-info"></i>
                        <span className="text-muted">Assigned To:</span> <span className="fw-bold text-dark ms-1">{selectedTicket?.assignedToName || "Unassigned"}</span>
                     </Col>
                     <Col md={4} className="text-md-end">
                        <i className="bx bx-calendar me-1 text-info"></i>
                        <span className="text-muted">Logged:</span> <span className="fw-bold text-dark ms-1">{selectedTicket && new Date(selectedTicket.createdDate).toLocaleString()}</span>
                     </Col>
                  </Row>
               </div>
               
               <div className="p-4" style={{ maxHeight: '450px', overflowY: 'auto', background: '#f8f9fa' }}>
                  <h6 className="text-muted text-uppercase fw-bold small mb-4">
                     <i className="bx bx-comment-detail me-2"></i>Communication History
                  </h6>
                  {viewLoading ? (
                    <div className="text-center py-5">
                       <i className="bx bx-loader bx-spin display-4 text-primary"></i>
                       <p className="mt-2 text-muted">Fetching conversation history...</p>
                    </div>
                  ) : (
                    <div className="chat-history">
                       {ticketLogs.length === 0 ? (
                         <div className="text-center py-5 border rounded bg-white mt-2">
                            <i className="bx bx-chat display-4 text-light mb-2"></i>
                            <div className="text-muted small italic">No comments or activity recorded yet.</div>
                         </div>
                       ) : (
                         ticketLogs.map((log, idx) => {
                            // Dynamic color assignment based on userId
                            const colorPool = ['primary', 'success', 'warning', 'danger', 'info', 'secondary'];
                            const colorScheme = colorPool[log.actionBy % colorPool.length];
                            const isMe = log.actionBy === session.userId;

                            return (
                               <div key={idx} className={`d-flex mb-4 ${log.comments ? 'flex-column' : 'justify-content-center'}`}>
                                  {log.comments ? (
                                    <>
                                       <div className={`d-flex align-items-center mb-1 ${isMe ? 'flex-row-reverse' : ''}`}>
                                          <div className="avatar-xs mx-2">
                                             <span className={`avatar-title rounded-circle bg-soft-${colorScheme} text-${colorScheme} fw-bold`}>
                                                {log.actionByName?.substring(0, 1).toUpperCase() || "U"}
                                             </span>
                                          </div>
                                          <div>
                                             <span className={`fw-bold small text-${colorScheme}`}>{log.actionByName}</span>
                                             <span className="text-muted tiny mx-2" style={{fontSize: '0.65rem'}}>{new Date(log.actionDate).toLocaleString([], {hour: '2-digit', minute:'2-digit', month:'short', day:'numeric'})}</span>
                                          </div>
                                       </div>
                                       <div className={`p-3 rounded-lg shadow-sm border-0 ${isMe ? 'bg-white text-dark ms-auto' : 'bg-white text-dark me-auto'} border-start border-4 border-${colorScheme}`} 
                                          style={{ 
                                             maxWidth: '85%', 
                                             width: 'fit-content',
                                             borderRadius: '12px',
                                             boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                          }}>
                                          <div className="fw-medium mb-1">{log.comments}</div>
                                          {log.actionDescription && (
                                             <div className="mt-2 pt-1 border-top small italic text-muted d-flex align-items-center" style={{fontSize: '0.75rem'}}>
                                                <i className="bx bx-info-circle me-1"></i> {log.actionDescription}
                                             </div>
                                          )}
                                       </div>
                                    </>
                                  ) : (
                                    <div className="badge bg-soft-secondary text-muted border-0 px-3 py-1 rounded-pill small fw-normal">
                                       <i className="bx bx-history me-1"></i> {log.actionDescription} - {new Date(log.actionDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                    </div>
                                  )}
                               </div>
                            )
                         })
                       )}
                    </div>
                  )}
               </div>
            </ModalBody>
          </Modal>

        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(GmsMaintenanceTicketsPage);
